import { Component, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms'
import { Title } from '@angular/platform-browser'
import { zip } from 'rxjs'

import { ReservationService } from 'app/shared/reservation/reservation.service'
import { IReservation } from 'app/shared/models/reservation.interfaces'
import { IVehicleWire, IVehicle, IVehicleSelection, ITrailer, IVehicleEditSelection } from 'app/vehicles/vehicles.interfaces'
import { ResourceService } from 'app/shared/resources/resource.service'
import { VehicleService } from 'app/vehicles/vehicle.service'
import { AmendService } from 'app/shared/reservation/amend.service'
import { SnapshotService } from 'app/shared/snapshot.service'
import { IInputVehicleDetailsRows } from 'app/vehicles/vehicles-details-rows/vehicles-details-rows.component'
import { IInputVehicleDetailsRow } from 'app/vehicles/vehicles-details-row/vehicles-details-row.component'
import { LayoutState } from '../../shared/layout/layout-state'
import { TrackingAmendmentStep } from '../../shared/tracking/tracking-wire.interfaces'
import { TrackingService } from '../../shared/tracking/tracking.service'
import { hashObject } from 'app/shared/utils';

@Component({
    selector: 'sbw-amendment-vehicles',
    templateUrl: './amendment-vehicles.component.html',
    styleUrls: ['./amendment-vehicles.component.css']
})
export class AmendmentVehiclesComponent implements OnInit {
    public amendmentsForm: FormGroup = new FormGroup({})
    public vehicleWire: IVehicleWire;
    private selectedVehicle: IVehicle
    private selectedTrailer: ITrailer
    private vehicles: IVehicle[]
    private trailers: ITrailer[]
    private vehicleSupportMultiple = true
    public reservationCode: string
    public errors: string[] = []
    public isAgent: boolean
    public vehiclesChanged = false
    private initialMd5
    private selections

    public reservation: IReservation
    inputVehiclesDetailsRows: IInputVehicleDetailsRows
    isAmendmentCompatible = true

    get selector(): FormGroup {
        return this.amendmentsForm.get('selector') as FormGroup
    }

    get vehiclesForm(): FormControl {
        return this.amendmentsForm.get('vehicles') as FormControl
    }

    constructor(
        private route: ActivatedRoute,
        private reservationService: ReservationService,
        private amendService: AmendService,
        private snapshotService: SnapshotService,
        private resourceService: ResourceService,
        private vehicleService: VehicleService,
        private title: Title,
        private fb: FormBuilder,
        private router: Router,
        private trackingService: TrackingService,
        private layoutState: LayoutState
    ) { }


    ngOnInit() {
        this.isAgent = localStorage.getItem('sbw_AgentGenericId') ? true : false;

        this.reservationCode = this.route.snapshot.params['reservationCode'];
        let reservation$ = this.reservationService.getReservation(this.reservationCode)
        let vehicles$ = this.vehicleService.getVehiclesFromReservation(this.reservationCode)

        zip(reservation$, vehicles$).subscribe(resArray => {
            let reservation = resArray[0]
            let vehicles = resArray[1]

            // 0 - reservation
            this.reservation = reservation
            this.loadResources(this.reservation.routeCode, this.reservation.locale)

            // 1 - vehicles
            this.vehicleWire = vehicles
            this.vehicleSupportMultiple = vehicles.supportMultiple
            let snapshot = this.snapshotService.getSnapshot(this.reservationCode)
            if (snapshot && snapshot.vehicles) {
                this.vehicleWire.selection.selections = snapshot.vehicles.selections
            }
            this.buildForm()
            this.trackingService.trackAmendment(TrackingAmendmentStep.VECHILES, false, this.reservation)

            this.layoutState.setIsContentLoaded(true)
        })
    }

    private loadResources(routeCode: string, locale: string): void {
        this.resourceService.loadResources(routeCode, locale, ['Common', 'Amendment', 'Currency', 'Quote', 'Passengers', 'Travel'].concat(this.isAgent ? ['Agent', 'MenuAgent'] : []))
        this.resourceService.get('TITLE_EDIT_VEHICLE_REGISTRATION', false).subscribe(s => this.title.setTitle(s));
    }

    private buildForm(): void {
        let { trailers, vehicles, selection } = this.vehicleWire
        this.vehicles = vehicles
        this.trailers = trailers

        if (!this.vehicleWire.isAmendmentCompatible) {
            this.isAmendmentCompatible = false
            this.resourceService.get('AMENDMENT_VEHICLE_INCOMPATIBLE').subscribe(s => this.errors = [s])
        }
        let selections = selection && selection.selections || []
        let selectedVehicleAndTrailer = selections && selections.length && selections[0]
        let selectedVehicle = vehicles.find(f => f.id === selectedVehicleAndTrailer.vehicleId)
        let hasSelections = selections && selections.length !== 0
        if (selectedVehicle) {
            this.selectedVehicle = selectedVehicle
            if (selectedVehicle.trailers) {
                if (selectedVehicleAndTrailer.trailerId) {
                    this.selectedTrailer = this.trailers.find(f => f.id === selectedVehicleAndTrailer.trailerId)
                } else {
                    this.selectedTrailer = this.trailers[0]
                }
            }
        } else {
            this.selectedVehicle = this.vehicles[0]
            this.selectedTrailer = this.trailers[0]
            selections.push({ vehicleId: this.selectedVehicle.id, vehicleRegistrationNumber: null, trailerId: this.selectedTrailer.id, trailerRegistrationNumber: null })
        }

        const formControlVehicle = new FormControl(this.selectedVehicle, [Validators.required])
        const formControlTrailer = new FormControl(this.selectedTrailer)
        const formControlCount = new FormControl(selections.length, [Validators.required])

        this.amendmentsForm = this.fb.group({
            'selector': this.fb.group({
                'vehicle': formControlVehicle,
                'trailer': formControlTrailer,
                'count': formControlCount
            })
        })

        this.amendmentsForm.valueChanges.subscribe(() => this.vehicleChangedHandler())

        if (this.vehicleWire.isAmendmentCompatible) {
            this.inputVehiclesDetailsRows = <IInputVehicleDetailsRows>{
                form: this.amendmentsForm,
                vehicles: selections.map(vehicle => {
                    const { vehicleId, vehicleRegistrationNumber, trailerId, trailerRegistrationNumber } = vehicle
                    return <IInputVehicleDetailsRow>{
                        form: this.amendmentsForm,
                        vehicleId,
                        vehicleRegistrationNumber,
                        trailerId,
                        trailerRegistrationNumber
                    }
                })
            }

            formControlCount.valueChanges.subscribe(count => {
                const len = this.inputVehiclesDetailsRows.vehicles.length
                const vehicleId = formControlVehicle.value.id
                const addFn = () => {
                    this.inputVehiclesDetailsRows.vehicles.push(<IInputVehicleDetailsRow>{
                        vehicleId,
                        vehicleRegistrationNumber: null,
                        trailerId: formControlTrailer.value && formControlTrailer.value.id,
                        trailerRegistrationNumber: null
                    })
                }
                const removeFn = () => this.inputVehiclesDetailsRows.vehicles.pop()
                let diff = len - count
                let fn = diff > 0 ? removeFn : addFn
                for (let c = 0; c < Math.abs(diff); c++) {
                    fn() // Do add or remove
                }
            })
        }
    }

    public vehicleChangedHandler() {
        if (!this.vehiclesForm)
            return;

        if (!this.initialMd5 && this.vehiclesForm.value && this.vehiclesForm.value.length == this.vehicleWire.selection.selections.length) {
            this.initialMd5 = hashObject(this.vehiclesForm.value)
        }
        if (this.initialMd5) {
            this.vehiclesChanged = hashObject(this.vehiclesForm.value) !== this.initialMd5
        }
    }

    public continue() {
        this.layoutState.setIsContentLoaded(false)
        const dryRun = true

        let vehicle = <IVehicle>this.amendmentsForm.get('selector.vehicle').value
        let trailer = <ITrailer>this.amendmentsForm.get('selector.trailer').value
        let logicalCount = !vehicle.id ? 0 : this.amendmentsForm.value.selector.count

        const vehiclesAndTrailers = vehicle.registrationNumberIsMandatory ? <IInputVehicleDetailsRow[]>this.amendmentsForm.get('vehicles').value : Array.from({ length: logicalCount }, i => (<IInputVehicleDetailsRow>{}))
        const registrations = vehiclesAndTrailers.map(m => <IVehicleSelection>{ vehicleId: m.vehicleId, vehicleRegistrationNumber: m.vehicleRegistrationNumber, trailerId: m.trailerId, trailerRegistrationNumber: m.trailerRegistrationNumber })
        let editWire: IVehicleEditSelection = {
            isNoVehicleSelected: logicalCount === 0,
            selections: registrations
        }
        this.amendService.changeVehicles(this.reservationCode, editWire, dryRun)
            .subscribe(response => {
                if (response.errorMsgs && response.errorMsgs.length) {
                    this.errors = response.errorMsgs.map(m => m.message)
                    this.layoutState.setIsContentLoaded(true)
                    return
                }
                this.snapshotService.setSnapshot(this.reservationCode, { vehicles: editWire })
                this.navigateBack()
            })
    }

    navigateBack() {
        this.router.navigate(['/amendment/booking', this.reservationCode])
    }
}
